import { ReservationRequestDto } from "@/api/generated/generatedTypes";
import { calculateAge } from "@/utils/date";
import { Accordion, ActionIcon, Badge, Button, Group, Stack, Text } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useState } from "react";
import { MatchIndicators } from "../components/MatchIndicators";
import { PreferredLocationsDisplay } from "../components/PreferredLocationsDisplay";
import { PreferredTimesDisplay } from "../components/PreferredTimesDisplay";
import { MemberRatingCapsules } from "../components/RatingCapsules";
import { UserOverviewModal } from "../components/UserOverviewModal";
import { ReservationPreferencesSection } from "../ReservationPreferencesSection";
import { PersonalityTestSection } from "./PersonalityTestSection";
import { formatTime, StagingGroup } from "./types";

const GENDER_LABELS: Record<string, string> = {
  MALE: "남성",
  FEMALE: "여성",
  NON_BINARY: "논바이너리",
};

interface StagedRequestItemProps {
  request: ReservationRequestDto;
  stagingGroup: StagingGroup;
  onRemove: (requestId: number) => void;
  showPersonalityTest: boolean;
  onTogglePersonalityTest: () => void;
  showReservationPreferences: boolean;
  onToggleReservationPreferences: () => void;
}

export const StagedRequestItem = ({
  request,
  stagingGroup,
  onRemove,
  showPersonalityTest,
  onTogglePersonalityTest,
  showReservationPreferences,
  onToggleReservationPreferences,
}: StagedRequestItemProps) => {
  const [overviewRequest, setOverviewRequest] = useState<ReservationRequestDto | null>(null);

  const dateMatches = stagingGroup.selectedDate
    ? request.preferredTimes.some((time) => {
        const timeString = typeof time === "string" ? time : (time as Date).toISOString();
        return timeString === stagingGroup.selectedDate;
      })
    : false;

  const locationMatches = stagingGroup.selectedLocationId
    ? request.preferredLocations.some((loc) => loc.id === stagingGroup.selectedLocationId)
    : false;

  const age = calculateAge(request.user.birthDate);
  const gender = request.user.gender;

  return (
    <>
      <Accordion.Item value={request.id.toString()}>
        <Accordion.Control>
          <Group justify="space-between" wrap="nowrap">
            <div style={{ flex: 1 }}>
              <Group gap="xs" wrap="nowrap">
                <Text c="black" size="sm" fw={500}>
                  {request.user.name} ({request.user.nickname})
                </Text>
                {age !== null && (
                  <Text c="dimmed" size="xs">
                    {age}세
                  </Text>
                )}
                {gender && (
                  <Text c="dimmed" size="xs">
                    {GENDER_LABELS[gender] ?? gender}
                  </Text>
                )}
                {request.user.isTestUser && (
                  <Badge size="xs" color="grape">
                    TEST
                  </Badge>
                )}
              </Group>
              <Group gap="sm" wrap="nowrap">
                <MatchIndicators dateMatches={dateMatches} locationMatches={locationMatches} />
                <MemberRatingCapsules uid={request.user.uid} stagingGroup={stagingGroup} />
              </Group>
            </div>
            <Group gap="xs" wrap="nowrap">
              <ActionIcon
                variant="subtle"
                size="lg"
                onClick={(e) => {
                  e.stopPropagation();
                  setOverviewRequest(request);
                }}
              >
                <IconSearch size={15} />
              </ActionIcon>
              <Button
                size="xs"
                variant="light"
                color="red"
                mr="xs"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(request.id);
                }}
              >
                제거
              </Button>
            </Group>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          <Stack gap="md" pl="md">
            <PreferredTimesDisplay
              preferredTimes={request.preferredTimes.map((t) =>
                typeof t === "string" ? t : (t as Date).toISOString()
              )}
              formatTime={formatTime}
              selectedDate={stagingGroup.selectedDate}
            />

            <PreferredLocationsDisplay
              preferredLocations={request.preferredLocations}
              selectedLocationId={stagingGroup.selectedLocationId}
            />

            <PersonalityTestSection
              requestId={request.id}
              answers={request.user.personalityTestAnswers}
              isVisible={showPersonalityTest}
              onToggle={onTogglePersonalityTest}
            />

            <Stack gap="xs">
              <Button
                variant="outline"
                size="xs"
                color="teal"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleReservationPreferences();
                }}
              >
                {showReservationPreferences ? "Reservation Info 숨기기" : "Reservation Info 보기"}
              </Button>

              {showReservationPreferences && (
                <ReservationPreferencesSection
                  preferredLanguage={request.preferredLanguage}
                  preferredCost={request.preferredCost}
                  dietaryRestrictionSlugs={request.dietaryRestrictionSlugs}
                  dietaryRestrictionsOtherText={request.dietaryRestrictionsOtherText}
                  messageForGuests={request.messageForGuests}
                />
              )}
            </Stack>
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>

      <UserOverviewModal request={overviewRequest} onClose={() => setOverviewRequest(null)} />
    </>
  );
};
